import {
  Check, Truck, Package, MapPin, ClipboardList, Award, Send, FileText, Flag, CircleCheck,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import type { DerivedStage } from "@/lib/lrDirect/deriveStages";

function compactDateTime(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const time = d
    .toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    .replace(" AM", "am")
    .replace(" PM", "pm")
    .replace(/\s/g, "");
  return `${date}, ${time}`;
}

function relativeAgo(iso: string | null): string {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function fullDate(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", {
    weekday: "short", month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

/** Per-stage glyph, matching the approved dispatch mockup. */
function stageIcon(stage: DerivedStage): LucideIcon {
  switch (stage.key) {
    case "posted": return Send;
    case "awarded": return Award;
    case "dispatch_created": return ClipboardList;
    case "truck_en_route": return Truck;
    case "in_transit": return Truck;
    case "delivered": return CircleCheck;
    case "pod_uploaded": return FileText;
    case "closed": return Flag;
    default: break;
  }
  if (stage.kind === "broker_stop") {
    if (stage.subKind === "checked_in") return stage.stopType === "pickup" ? Truck : MapPin;
    return stage.stopType === "pickup" ? Package : CircleCheck;
  }
  return Check;
}

interface Props {
  stages: DerivedStage[];
  delayed?: boolean;
  className?: string;
  /**
   * Optional per-stage click handler. When provided, the circle for that stage
   * renders as an interactive button (used by the broker to set/undo status).
   * Return `null` to fall back to the read-only rendering.
   */
  renderStageButton?: (stage: DerivedStage, defaultButton: React.ReactElement) => React.ReactElement | null;
  /**
   * Key of the single NEXT UP milestone (first incomplete actionable stage).
   * Rendered with the amber "next action" treatment so the tracker and the
   * NEXT UP button always point at the same step.
   */
  nextKey?: string | null;
  /**
   * "dispatch" = the approved broker/carrier dispatch mockup treatment:
   * larger circles carrying a per-stage glyph, teal completed rail, orange
   * current/next milestone, light-gray future milestones.
   */
  appearance?: "default" | "dispatch";
}

export function LoadStageTracker({
  stages, delayed, className, renderStageButton, nextKey, appearance = "default",
}: Props) {
  const hasAnyHeader = stages.some((s) => s.groupHeader) || !!nextKey;
  const mock = appearance === "dispatch";
  return (
    <TooltipProvider delayDuration={150}>
      {/* Horizontal layout (≥ md) */}
      <div className={cn("hidden md:block w-full", className)}>
        {delayed && (
          <div className="mb-2 flex justify-center">
            <Badge variant="outline" className="bg-amber-500/10 text-amber-700 border-amber-500/40 text-[10px] uppercase font-bold">
              Delayed
            </Badge>
          </div>
        )}

        {/* Group-header row (above the rail) — reserves the same height across
            every column so single-word stages line up vertically with per-stop
            stages that carry a "PICKUP 1" header. */}
        {hasAnyHeader && (
          <div className="flex items-end w-full mb-1">
            {stages.map((stage) => (
              <div key={`hdr-${stage.key}`} className="flex-1 px-1 text-center min-w-0">
                <div
                  className={cn(
                    "text-[9px] font-bold uppercase tracking-[0.12em] leading-none truncate",
                    stage.key === nextKey
                      ? mock ? "text-orange-600" : "text-amber-600"
                      : stage.groupHeader
                        ? "text-brand-teal"
                        : "text-transparent",
                  )}
                  aria-hidden={!stage.groupHeader && stage.key !== nextKey}
                >
                  {stage.key === nextKey ? "Next up" : stage.groupHeader || "\u00A0"}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Rail row */}
        <div className={cn("relative flex items-center w-full", mock ? "h-8" : "h-5")}>
          <div
            className="absolute top-1/2 -translate-y-1/2 h-0.5 bg-border"
            style={{ left: `calc(100% / ${stages.length * 2})`, right: `calc(100% / ${stages.length * 2})` }}
          />
          {stages.slice(0, -1).map((stage, i) => {
            const next = stages[i + 1];
            const active = stage.status === "done" && (next.status === "done" || next.status === "current");
            const isDelayedSeg = delayed && stage.status === "done" && next.status === "current";
            if (!active && !isDelayedSeg) return null;
            const segWidth = 100 / stages.length;
            const left = segWidth * (i + 0.5);
            return (
              <div
                key={`seg-${stage.key}`}
                className={cn("absolute top-1/2 -translate-y-1/2 h-0.5", isDelayedSeg ? "bg-amber-500" : "bg-brand-teal")}
                style={{ left: `${left}%`, width: `${segWidth}%` }}
              />
            );
          })}

          {stages.map((stage) => {
            const Icon = stageIcon(stage);
            const isNext = stage.key === nextKey && stage.status !== "done";
            const defaultButton = (
              <button
                type="button"
                className={cn(
                  "relative z-10 p-0 m-0 border-0 rounded-full inline-flex items-center justify-center leading-none appearance-none bg-background focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal focus-visible:ring-offset-1",
                  mock ? "h-8 w-8" : "h-5 w-5",
                  stage.status === "done" && "bg-brand-teal text-white",
                  stage.status === "current" && !mock && "ring-2 ring-brand-teal ring-offset-2 ring-offset-background",
                  stage.status === "current" && mock && !isNext && "border-2 border-brand-teal text-brand-teal",
                  stage.status === "future" && !mock && "border-2 border-border",
                  stage.status === "future" && mock && "border border-border text-muted-foreground/70",
                  stage.status === "skipped" && "bg-muted border border-border",
                  isNext && !mock && "ring-2 ring-amber-500 ring-offset-2 ring-offset-background border-0",
                  isNext && mock &&
                    "bg-orange-500 text-white border-0 ring-2 ring-orange-500/30 ring-offset-2 ring-offset-background shadow-[0_2px_8px_-2px_hsl(24_95%_53%/0.5)]",
                )}
              >
                {mock ? (
                  stage.status === "done"
                    ? <Check className="h-4 w-4" />
                    : <Icon className="h-3.5 w-3.5" />
                ) : (
                  <>
                    {stage.status === "done" && <Check className="h-3 w-3" />}
                    {isNext && <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />}
                    {stage.status === "current" && !isNext && (
                      <span className="h-1.5 w-1.5 rounded-full bg-brand-teal animate-pulse" />
                    )}
                  </>
                )}
              </button>
            );
            const interactive = renderStageButton?.(stage, defaultButton);
            return (
              <div key={stage.key} className="relative flex-1 flex items-center justify-center">
                {interactive ?? (
                  <Tooltip>
                    <TooltipTrigger asChild>{defaultButton}</TooltipTrigger>
                    <TooltipContent side="top" className="text-xs">
                      <div className="font-semibold">{stage.label}</div>
                      {stage.at ? (
                        <div className="text-muted-foreground">{fullDate(stage.at)}</div>
                      ) : stage.status === "current" ? (
                        <div className="text-muted-foreground">In progress</div>
                      ) : stage.status === "future" ? (
                        <div className="text-muted-foreground">Pending</div>
                      ) : null}
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            );
          })}
        </div>

        {/* Label row — short one-line label. Timestamp row lives beneath and
            stays on the same baseline across all columns because every label
            is a single line. */}
        <div className="flex items-start w-full mt-2">
          {stages.map((stage) => (
            <div key={`lbl-${stage.key}`} className="flex-1 px-1 text-center min-w-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className={cn(
                    "text-[10px] font-medium leading-tight truncate whitespace-nowrap",
                    stage.status === "done" && (mock ? "text-foreground font-semibold" : "text-foreground"),
                    stage.status === "current" && "text-brand-teal font-semibold",
                    stage.status === "future" && "text-muted-foreground",
                    stage.status === "skipped" && "text-muted-foreground line-through",
                    stage.key === nextKey && stage.status !== "done" &&
                      (mock ? "text-orange-600 font-semibold" : "text-amber-600 font-semibold"),
                  )}>
                    {stage.shortLabel || stage.label}
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  <div className="font-semibold">{stage.label}</div>
                </TooltipContent>
              </Tooltip>
              <div className="text-[9px] text-muted-foreground mt-0.5 tabular-nums leading-none min-h-[10px]">
                {stage.at ? compactDateTime(stage.at) : mock ? "—" : "\u00A0"}
              </div>
            </div>
          ))}
        </div>
      </div>


      {/* Vertical layout (mobile) — same interaction model as desktop:
          `renderStageButton` wraps the circle so brokers/carriers can open the
          StagePopover (mark done / undo / edit time) on a phone. Circles keep a
          44px tap area via the expanded pseudo-element hit box. */}
      <div className={cn("md:hidden", className)}>
        {delayed && (
          <Badge variant="outline" className="mb-2 bg-amber-500/10 text-amber-700 border-amber-500/40 text-[10px] uppercase font-bold">
            Delayed
          </Badge>
        )}
        <ol className="relative border-l-2 border-border ml-4 space-y-4">
          {stages.map((stage) => {
            const Icon = stageIcon(stage);
            const isNext = stage.key === nextKey && stage.status !== "done";
            const defaultButton = (
              <button
                type="button"
                className={cn(
                  // 36px visual circle + a 44px invisible tap target
                  "relative h-9 w-9 p-0 m-0 border-0 appearance-none flex items-center justify-center rounded-full bg-background leading-none",
                  "before:absolute before:-inset-1 before:content-[''] before:rounded-full",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal focus-visible:ring-offset-1",
                  stage.status === "done" && "bg-brand-teal text-white",
                  stage.status === "current" && "border-2 border-brand-teal text-brand-teal",
                  stage.status === "future" && "border-2 border-border text-muted-foreground/70",
                  stage.status === "skipped" && "bg-muted border border-border",
                  isNext && (mock
                    ? "bg-orange-500 text-white border-0 ring-2 ring-orange-500/30"
                    : "bg-background ring-2 ring-amber-500 ring-offset-2 ring-offset-background border-0"),
                )}
              >
                {stage.status === "done"
                  ? <Check className="h-4 w-4" />
                  : <Icon className="h-4 w-4" />}
              </button>
            );
            const interactive = renderStageButton?.(stage, defaultButton);
            return (
            <li key={stage.key} className="pl-6 relative min-h-[36px]">
              <span className="absolute -left-[19px] top-0 z-10">
                {interactive ?? defaultButton}
              </span>
              <div className={cn(
                "text-sm font-medium",
                stage.status === "current" && "text-brand-teal font-semibold",
                stage.status === "future" && "text-muted-foreground",
                stage.status === "skipped" && "text-muted-foreground line-through",
                isNext && (mock ? "text-orange-600 font-semibold" : "text-amber-600 font-semibold"),
              )}>
                {stage.label}
                {isNext && (
                  <span className={cn(
                    "ml-2 align-middle text-[9px] font-bold uppercase tracking-[0.12em]",
                    mock ? "text-orange-600" : "text-amber-600",
                  )}>
                    Next up
                  </span>
                )}
              </div>
              {stage.at && (
                <div className="text-[11px] text-muted-foreground">
                  {compactDateTime(stage.at)} · <span className="text-muted-foreground/70">{relativeAgo(stage.at)}</span>
                </div>
              )}
            </li>
          );})}
        </ol>
      </div>

    </TooltipProvider>
  );
}
